import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { executeDraw, getDraws, sendNotification } from '@/api/Lottery';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import type { Draw, Lottery } from '@/types/Lottery';
import CreateDraw from '../CreateDraw';

interface DrawListProps {
  lotteryId: string;
  lottery: Lottery;
}

const DrawList: React.FC<DrawListProps> = ({ lotteryId, lottery }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draws, setDraws] = useState<Draw[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runDrawLoadingId, setRunDrawLoadingId] = useState<string | null>(null);
  const [runDrawError, setRunDrawError] = useState<string | null>(null);
  const [runDrawSuccess, setRunDrawSuccess] = useState<string | null>(null);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const [notificationSuccess, setNotificationSuccess] = useState<string | null>(null);
  const [individualNotificationLoading, setIndividualNotificationLoading] = useState<string | null>(null);

  const fetchDraws = useCallback(() => {
    setIsLoading(true);
    setError(null);
    getDraws(lotteryId)
      .then((res) => {
        setDraws(res.data.data || []);
      })
      .catch(() => {
        setError('Failed to load draws');
      })
      .finally(() => setIsLoading(false));
  }, [lotteryId]);

  useEffect(() => {
    fetchDraws();
  }, [fetchDraws]);

  const handleRunDraw = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchDraws();
  };

  const handleRunDrawRow = async (drawId: string) => {
    setRunDrawLoadingId(drawId);
    setRunDrawError(null);
    setRunDrawSuccess(null);
    try {
      await executeDraw(drawId);
      setRunDrawSuccess('Draw executed successfully!');
      fetchDraws();
    } catch (err: any) {
      let errorMsg = 'Failed to execute draw.';
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setRunDrawError(errorMsg);
    } finally {
      setRunDrawLoadingId(null);
      setTimeout(() => setRunDrawSuccess(null), 2000);
    }
  };

  const handleSendNotification = async () => {
    setIsNotificationLoading(true);
    setNotificationError(null);
    setNotificationSuccess(null);

    try {
      // Get all draw IDs from the draws array
      const drawIds = draws.map((draw) => parseInt(draw.id, 10));

      await sendNotification(lotteryId, { drawIds });
      setNotificationSuccess('Notification sent successfully!');
      fetchDraws(); // Refresh to update notification status
    } catch (err: any) {
      let errorMsg = 'Failed to send notification.';
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setNotificationError(errorMsg);
    } finally {
      setIsNotificationLoading(false);
      setTimeout(() => setNotificationSuccess(null), 3000);
    }
  };

  const handleSendIndividualNotification = async (drawId: string) => {
    setIndividualNotificationLoading(drawId);
    setNotificationError(null);
    setNotificationSuccess(null);

    try {
      await sendNotification(lotteryId, { drawIds: [parseInt(drawId, 10)] });
      setNotificationSuccess('Notification sent successfully!');
      fetchDraws(); // Refresh to update notification status
    } catch (err: any) {
      let errorMsg = 'Failed to send notification.';
      if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setNotificationError(errorMsg);
    } finally {
      setIndividualNotificationLoading(null);
      setTimeout(() => setNotificationSuccess(null), 3000);
    }
  };

  return (
    <>
      {isModalOpen && (
        <Modal customStyle='z-[200]' width='max-w-2xl'>
          <div className='p-2'>
            <CreateDraw lotteryId={lotteryId} onSuccess={handleCloseModal} />
          </div>
        </Modal>
      )}
      <section aria-labelledby='draw-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='flex items-center justify-between px-4 py-5 sm:px-6'>
            <div>
              <h2 id='draw-information-title' className='text-lg font-medium leading-6 text-gray-900'>
                Draws
              </h2>
              <p className='mt-1 max-w-2xl text-sm text-gray-500'>List of draws for this lottery.</p>
            </div>
            <div className='flex gap-3'>
              <Button
                onClick={handleSendNotification}
                label={isNotificationLoading ? 'Sending...' : 'Send Notification'}
                btnStyle='secondary'
                disabled={isNotificationLoading || draws.length === 0}
              />
              <Button onClick={handleRunDraw} label='Create Draw' btnStyle='primary' />
            </div>
          </div>
          <div className='border-t border-gray-200'>
            {runDrawError && <div className='p-2 text-red-600 text-sm'>{runDrawError}</div>}
            {runDrawSuccess && <div className='p-2 text-green-600 text-sm'>{runDrawSuccess}</div>}
            {notificationError && <div className='p-2 text-red-600 text-sm'>{notificationError}</div>}
            {notificationSuccess && <div className='p-2 text-green-600 text-sm'>{notificationSuccess}</div>}
            {isLoading ? (
              <p className='text-gray-500 p-4'>Loading draws...</p>
            ) : error ? (
              <p className='text-red-500 p-4'>{error}</p>
            ) : draws.length === 0 ? (
              <p className='text-gray-500 p-4'>No draws yet.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full w-full table-auto divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Draw Name
                      </th>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Created Date
                      </th>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Draw Executed Date
                      </th>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Total Winners
                      </th>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Status
                      </th>
                      <th scope='col' className='px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Notification
                      </th>
                      <th scope='col' className='relative px-3 sm:px-6 py-3'>
                        <span className='sr-only'>View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {draws.map((draw) => (
                      <tr key={draw.id}>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>{draw.name}</td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {draw.createdAt ? new Date(draw.createdAt).toLocaleString() : '-'}
                        </td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                          {draw.drawExecuteDate ? new Date(draw.drawExecuteDate).toLocaleString() : '-'}
                        </td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500'>{draw.totalWinners ?? '-'}</td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm'>
                          {draw.status ? (
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                draw.status === 'completed'
                                  ? 'bg-green-100 text-green-800'
                                  : draw.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {draw.status.charAt(0).toUpperCase() + draw.status.slice(1)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-sm'>
                          {draw.notificationSent ? (
                            <span className='px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800'>Sent</span>
                          ) : (
                            <span className='px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-800'>Not Sent</span>
                          )}
                        </td>
                        <td className='px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                          <div className='flex items-center justify-end gap-2'>
                            <Link
                              to={buildRoute(AppRoutes.LOTTERY_DRAW_DETAIL, { lotteryId, drawId: draw.id })}
                              className='inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            >
                              View
                            </Link>
                            <button
                              type='button'
                              onClick={() => handleSendIndividualNotification(draw.id)}
                              disabled={individualNotificationLoading === draw.id || draw.notificationSent}
                              className='inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              {individualNotificationLoading === draw.id ? 'Sending...' : 'Send Notification'}
                            </button>
                            {draw.status && !['executed', 'completed'].includes(draw.status.toLowerCase()) ? (
                              <button
                                type='button'
                                onClick={() => handleRunDrawRow(draw.id)}
                                disabled={runDrawLoadingId === draw.id}
                                className='inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                {runDrawLoadingId === draw.id ? 'Running...' : 'Run Draw'}
                              </button>
                            ) : (
                              <div className='inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-400 bg-gray-100 cursor-not-allowed'>
                                Executed
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default DrawList;
